import apiClient from './apiClient';
import type {
  FeedbackTicket,
  FeedbackTicketDetail,
  FeedbackTicketStatus,
  FeedbackAttachment,
  FeedbackComment,
  CreateFeedbackRequest,
  FeedbackUploadUrlResponse,
  FeedbackStats,
} from '../types/feedback';

export const submitFeedback = async (data: CreateFeedbackRequest): Promise<FeedbackTicket> => {
  const response = await apiClient.post<FeedbackTicket>('/feedback', data);
  return response.data;
};

export const getUploadUrl = async (
  fileName: string,
  contentType: string,
): Promise<FeedbackUploadUrlResponse> => {
  const response = await apiClient.post<FeedbackUploadUrlResponse>('/feedback/upload-url', {
    fileName,
    contentType,
  });
  return response.data;
};

export const getMyTickets = async (): Promise<FeedbackTicket[]> => {
  const response = await apiClient.get<FeedbackTicket[]>('/feedback/mine');
  return response.data;
};

export const getMyTicketDetail = async (id: string): Promise<FeedbackTicketDetail> => {
  const response = await apiClient.get<FeedbackTicketDetail>(`/feedback/mine/${id}`);
  return response.data;
};

export const getAllTickets = async (): Promise<FeedbackTicket[]> => {
  const response = await apiClient.get<FeedbackTicket[]>('/feedback');
  return response.data;
};

export const getTicketDetail = async (id: string): Promise<FeedbackTicketDetail> => {
  const response = await apiClient.get<FeedbackTicketDetail>(`/feedback/${id}`);
  return response.data;
};

export const updateTicket = async (
  id: string,
  data: Record<string, unknown>,
): Promise<FeedbackTicket> => {
  const response = await apiClient.patch<FeedbackTicket>(`/feedback/${id}`, data);
  return response.data;
};

export const getFeedbackStats = async (): Promise<FeedbackStats[]> => {
  const response = await apiClient.get<FeedbackStats[]>('/feedback/stats');
  return response.data;
};

/** Admin-only: delete any ticket */
export const deleteTicket = async (id: string): Promise<void> => {
  await apiClient.delete(`/feedback/${id}`);
};

/** Any user: delete their OWN ticket */
export const deleteMyTicket = async (id: string): Promise<void> => {
  await apiClient.delete(`/feedback/mine/${id}`);
};

/** Submitter: edit own ticket (title/description/severity/priority). Only when status is New or In Progress. */
export const updateMyTicket = async (
  id: string,
  data: { Title?: string; Description?: string | null; Severity?: string | null; Priority?: string | null },
): Promise<FeedbackTicket> => {
  const response = await apiClient.patch<FeedbackTicket>(`/feedback/mine/${id}`, data);
  return response.data;
};

/** Submitter: add attachments to own ticket */
export const addMyAttachments = async (
  id: string,
  attachments: { FileName: string; BlobUrl: string; FileSize: number; ContentType: string }[],
): Promise<FeedbackAttachment[]> => {
  const response = await apiClient.post<FeedbackAttachment[]>(`/feedback/mine/${id}/attachments`, { attachments });
  return response.data;
};

/** Submitter: remove one attachment from own ticket */
export const deleteMyAttachment = async (attachmentKey: string): Promise<void> => {
  await apiClient.delete(`/feedback/mine/attachments/${attachmentKey}`);
};

/** Submitter: drive a status transition allowed by the submitter
 *  (e.g. Reject → Needs Rework, Confirm → Confirmed Fixed). `Note` is folded
 *  into the comments thread when provided. */
export const updateMyTicketStatus = async (
  id: string,
  Status: FeedbackTicketStatus,
  Note?: string,
): Promise<FeedbackTicket> => {
  const response = await apiClient.patch<FeedbackTicket>(`/feedback/mine/${id}/status`, { Status, Note });
  return response.data;
};

/** Admin: list comments on any ticket */
export const listComments = async (id: string): Promise<FeedbackComment[]> => {
  const response = await apiClient.get<FeedbackComment[]>(`/feedback/${id}/comments`);
  return response.data;
};

/** Admin: add a comment to any ticket */
export const addComment = async (id: string, body: string): Promise<FeedbackComment> => {
  const response = await apiClient.post<FeedbackComment>(`/feedback/${id}/comments`, { Body: body });
  return response.data;
};

/** Submitter: list comments on own ticket */
export const listMyComments = async (id: string): Promise<FeedbackComment[]> => {
  const response = await apiClient.get<FeedbackComment[]>(`/feedback/mine/${id}/comments`);
  return response.data;
};

/** Submitter: add a comment to own ticket */
export const addMyComment = async (id: string, body: string): Promise<FeedbackComment> => {
  const response = await apiClient.post<FeedbackComment>(`/feedback/mine/${id}/comments`, { Body: body });
  return response.data;
};

/** Upload a file directly to Azure Blob Storage via SAS URL */
export function uploadToBlob(
  uploadUrl: string,
  file: File,
  onProgress?: (pct: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', uploadUrl, true);
    xhr.setRequestHeader('x-ms-blob-type', 'BlockBlob');
    xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) {
        onProgress(Math.round((e.loaded / e.total) * 100));
      }
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`Upload failed with status ${xhr.status}`));
    };

    xhr.onerror = () => reject(new Error('Upload failed'));
    xhr.send(file);
  });
}
